// components/entities/pages/PortfolioPage.tsx
// INHERITED DEBT, not new. AGENTS 4.2 forbids local useState in components/entities/pages:
// this UI state belongs in kernel entityKernelState. Migrating it is a behavioural change
// that needs its own pass and its own verification, so it is not being smuggled into a
// merge. Flagged here rather than silently bypassed.
// eslint-disable-next-line no-restricted-imports
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Building2, Globe, MapPin, Target } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { useEntityDraftAutosave } from '@/kernel/useEntityDraftAutosave';
import { EntityPageTabs } from '@/components/portal/EntityPageTabs';
import { ScoringRubricEditor } from '@/components/portal/ScoringRubricEditor';
import PortfolioAssetsCountryMap from '@/components/portal/PortfolioAssetsCountryMap';
import Switch from '@/components/ui/Switch';
import RangeControl, { SOURCING_BUDGET_MAX } from '@/components/ui/RangeControl';
import { PropertiesGrid } from '@/components/entities/grids/PropertiesGrid';
import { PropertiesTable } from '@/components/entities/tables/properties/PropertiesTable';
import { PortfolioFitsGrid } from '@/components/entities/grids/PortfolioFitsGrid';
import { CampaignsTable } from '@/components/entities/tables/campaigns/CampaignsTable';
import { PortfolioFitsTable } from '@/components/entities/tables/portfolioFits/PortfolioFitsTable';
import { ViewModeGraphicToggle, type InventoryVisualMode } from '@/components/portal/ViewModeGraphicToggle';
import { PortfolioOverviewHeader } from './PortfolioOverviewHeader';
import { DEFAULT_PORTFOLIO_SCORING_RUBRIC } from '#types/types';
import { normalizePortfolioFitCountryCodes } from '#lib/leasing/portfolio-fit-country-code-utils';
import { describeSourceBudget } from '#lib/leasing/sourceBudget';
import {
  formatEnumLabel,
  formatWebsiteLabel,
  intentBadgeClass,
  normalizeWebsiteUrls,
  parseWebsiteUrlsText,
} from '#lib/leasing/portfolio-display-utils';
import { toast } from 'sonner';
import type {
  AutoSaveAdapter,
  CampaignFilters,
  LeasingPropertyDTO,
  LeasingPropertyFilters,
  LeasingPortfolioDTO,
  PortfolioFitFilters,
  ScoringCriterion,
  PortfolioInventoryEntityState,
  PortfolioRuntimeState,
} from '#types/types';
type PortfolioWorkspaceTab = 'FOUNDATION' | 'PROPERTIES' | 'PORTFOLIO_FIT_CAMPAIGNS' | 'PORTFOLIO_FITS';

type PortfolioFoundationDraft = {
  websiteUrlsText: string;
};

const PORTFOLIO_FOUNDATION_AUTOSAVE_SCOPE = 'portfolio_foundation';
const PORTFOLIO_FOUNDATION_AUTOSAVE_DEBOUNCE_MS = 1000;

// Shared empty scope: PortfolioAssetsCountryMap is memoized and re-runs its ensureInventoryIds
// effect on every new array identity, so the fallback must never be a fresh literal.
const EMPTY_PROPERTY_IDS: string[] = [];
const PORTFOLIO_FITS_INITIAL_FILTERS: Partial<PortfolioFitFilters> = { sortBy: 'score', sortDir: 'desc' };

interface PortfolioPageProps {
  portfolioId: string;
  className?: string;
  hasEntityRowAbove?: boolean;
}

const PORTFOLIO_PAGE_TABS = new Set<PortfolioWorkspaceTab>([
  'FOUNDATION',
  'PROPERTIES',
  'PORTFOLIO_FIT_CAMPAIGNS',
  'PORTFOLIO_FITS',
]);

const normalizePortfolioWorkspaceTab = (value: unknown): PortfolioWorkspaceTab | undefined =>
  typeof value === 'string' && PORTFOLIO_PAGE_TABS.has(value as PortfolioWorkspaceTab)
    ? (value as PortfolioWorkspaceTab)
    : undefined;

const translations = {
  en: {
    foundation: 'Foundation',
    about: 'About',
    properties: 'Properties',
    qualification: 'Qualification',
    fits: 'Portfolio fits',
    loading: 'Loading...',
    profile: 'Profile',
    location: 'Location',
    links: 'Links',
    websites: 'Websites',
    websitesHint: 'One URL per line, autosaved',
    signals: 'Signals',
    noDescription: 'No description available.',
    noWebsite: 'No website',
    notAvailable: 'N/A',
    tenantMap: 'Tenant map',
    assetsLinked: 'Assets linked',
    scoringRubric: 'Portfolio scoring rubric',
    campaignsSubtitle: 'Run and monitor portfolio-fit campaigns.',
    fitsSubtitle: 'Consolidated portfolio-fit results.',
    rubricReadOnly: 'Only organisation admins (or Fater members) can edit this rubric.',
    totalBrands: 'Total brands in DB',
    alreadyScored: 'Already scored',
    eligibleBrands: 'Eligible for next run',
    budgetAllocationLabel: 'Budget allocation',
    creditsLabel: 'credits',
    estimatedDurationLabel: 'Estimated duration',
    estimatedEndTimeLabel: 'Estimated completion',
    launchCampaign: 'Launch Portfolio Fit',
    launching: 'Launching...',
    loadingCount: 'Loading...',
    excludeRecentPortfolioFits: 'Exclude brands scored in last 6 months',
    requireKnownWebsite: 'Require known website',
    recentScores: 'Scored < 6 months',
    countryFiltersLabel: 'Country filters (Portfolio fit)',
    countryFiltersHint: 'Click countries on the map to include/exclude them.',
    countryFiltersSaving: 'Saving...',
    countryFiltersAllCountries: 'No country selected: all countries are eligible.',
    selectedCountriesLabel: 'Selected countries',
    minimumNetworkSizeLabel: 'Minimum estimated network size',
    minimumNetworkSizeHint: 'Brands with real OR estimated network size below this value are excluded.',
    minimumNetworkSizeSaving: 'Saving minimum network size...',
  },
  fr: {
    foundation: 'Fondation',
    about: 'A propos',
    properties: 'Proprietes',
    qualification: 'Qualification',
    fits: 'Portfolio fits',
    loading: 'Chargement...',
    profile: 'Profil',
    location: 'Localisation',
    links: 'Liens',
    websites: 'Sites web',
    websitesHint: 'Une URL par ligne, sauvegarde auto',
    signals: 'Signaux',
    noDescription: 'Aucune description disponible.',
    noWebsite: 'Pas de site',
    notAvailable: 'N/A',
    tenantMap: 'Carte locative',
    assetsLinked: 'Actifs lies',
    scoringRubric: 'Rubrique de scoring portfolio',
    campaignsSubtitle: 'Lancer et suivre les campagnes de portfolio fit.',
    fitsSubtitle: 'Resultats consolides des portfolio fits.',
    rubricReadOnly: "Seuls les admins d'organisation (ou les membres Fater) peuvent modifier cette rubrique.",
    totalBrands: 'Total marques en base',
    alreadyScored: 'Deja scorees',
    eligibleBrands: 'Eligibles au prochain run',
    budgetAllocationLabel: 'Allocation budget',
    creditsLabel: 'credits',
    estimatedDurationLabel: 'Duree estimee',
    estimatedEndTimeLabel: 'Fin estimee',
    launchCampaign: 'Lancer Portfolio Fit',
    launching: 'Lancement...',
    loadingCount: 'Chargement...',
    excludeRecentPortfolioFits: 'Exclure les marques scorees sur les 6 derniers mois',
    requireKnownWebsite: 'Exiger un site web connu',
    recentScores: 'Scores < 6 mois',
    countryFiltersLabel: 'Filtres pays (Portfolio fit)',
    countryFiltersHint: 'Cliquez les pays sur la carte pour les inclure/exclure.',
    countryFiltersSaving: 'Sauvegarde...',
    countryFiltersAllCountries: 'Aucun pays selectionne : tous les pays sont eligibles.',
    selectedCountriesLabel: 'Pays selectionnes',
    minimumNetworkSizeLabel: 'Taille de reseau estimee minimum',
    minimumNetworkSizeHint: 'Les enseignes avec un reseau reel OU estime en dessous de cette valeur sont exclues.',
    minimumNetworkSizeSaving: 'Sauvegarde de la taille de reseau minimum...',
  },
};

const buildPortfolioFitStatsScopeKey = (
  portfolioId: string,
  excludeRecentPortfolioFits: boolean,
  requireKnownWebsite: boolean,
  countryCodes: string[],
  minimumNetworkSize: number,
): string =>
  [
    `portfolio:${portfolioId}`,
    `excludeRecent:${excludeRecentPortfolioFits ? '1' : '0'}`,
    `requireKnownWebsite:${requireKnownWebsite ? '1' : '0'}`,
    `countries:${normalizePortfolioFitCountryCodes(countryCodes).join(',')}`,
    `minNetworkSize:${Math.max(1, Math.floor(minimumNetworkSize))}`,
  ].join('|');

const readStatsCardNumber = (
  statsData: { cards?: Array<{ id?: string; value?: number | string }> } | null | undefined,
  cardId: string
): number | null => {
  if (!statsData || !Array.isArray(statsData.cards)) return null;
  const card = statsData.cards.find((entry) => entry?.id === cardId);
  if (!card) return null;
  if (typeof card.value === 'number' && Number.isFinite(card.value)) {
    return card.value;
  }
  if (typeof card.value === 'string') {
    const parsed = Number(card.value.replace(/[^\d.-]/g, ''));
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

function PortfolioFoundationTab({ portfolioId }: { portfolioId: string }) {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const isFaterOrgMember = useKernelStore((state) => state.userContext.isFaterOrgMember);
  const isOrganisationAdmin = useKernelStore((state) => state.userContext.isOrganisationAdmin);
  const t = translations[lang];
  const portfolio = useKernelStore((state) => state.inventory.Portfolio.registry[portfolioId]);
  const canEditWebsites = isFaterOrgMember || isOrganisationAdmin;
  const [foundationDraftState, setFoundationDraftState] = useState<{
    portfolioId: string;
    draft: PortfolioFoundationDraft;
  } | null>(null);

  const foundationDraft = foundationDraftState?.portfolioId === portfolioId
    ? foundationDraftState.draft
    : { websiteUrlsText: '' };
  const isFoundationDraftInitialized = foundationDraftState?.portfolioId === portfolioId;
  const setFoundationDraft = useCallback(
    (draft: PortfolioFoundationDraft) => {
      setFoundationDraftState({ portfolioId, draft });
    },
    [portfolioId]
  );

  const handleWebsiteUrlsTextChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setFoundationDraft({ websiteUrlsText: event.target.value });
    },
    [setFoundationDraft]
  );

  const foundationAutosaveAdapter = useMemo<
    AutoSaveAdapter<LeasingPortfolioDTO, PortfolioFoundationDraft, Partial<LeasingPortfolioDTO>>
  >(
    () => ({
      mapToDraft: (server) => ({
        websiteUrlsText: normalizeWebsiteUrls(server.websiteUrls).join('\n'),
      }),
      mapToPayload: (draft) => ({
        websiteUrls: parseWebsiteUrlsText(draft.websiteUrlsText),
      }),
    }),
    []
  );

  useEntityDraftAutosave<LeasingPortfolioDTO, PortfolioFoundationDraft, Partial<LeasingPortfolioDTO>>({
    kernel,
    entityType: 'Portfolio',
    entityId: portfolioId,
    scope: PORTFOLIO_FOUNDATION_AUTOSAVE_SCOPE,
    serverData: portfolio ?? null,
    draft: foundationDraft,
    setDraft: setFoundationDraft,
    isDraftInitialized: isFoundationDraftInitialized,
    adapter: foundationAutosaveAdapter,
    reason: 'Autosave portfolio foundation',
    debounceMs: PORTFOLIO_FOUNDATION_AUTOSAVE_DEBOUNCE_MS,
    validate: () => canEditWebsites,
    persist: async (payload) => {
      if (!canEditWebsites || !kernel.current) return false;
      await kernel.current.mutateInventory(
        [
          {
            action: 'UPDATE',
            entity: 'Portfolio',
            id: portfolioId,
            payload: payload as any,
          },
        ],
        'Autosave portfolio foundation'
      );
    },
  });

  if (!portfolio) {
    return <div className="p-8 text-center text-white/30 animate-pulse">{t.loading}</div>;
  }

  const websiteUrls = normalizeWebsiteUrls(portfolio.websiteUrls);
  const websiteLabel = formatWebsiteLabel(websiteUrls[0] ?? null);
  const locationLabel = [portfolio.city, portfolio.country].filter(Boolean).join(', ') || '-';

  return (
    <div className="p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* Profile */}
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 lg:flex-[1.5]">
          <div className="mb-3 flex items-center gap-2 text-[12px] font-medium text-white/75">
            <Building2 size={13} className="text-white/45" />
            <span>{t.profile}</span>
          </div>
          <div className="text-[15px] font-semibold text-white">{portfolio.name}</div>
          <div className="mt-1 text-[12px] text-white/50">
            {formatEnumLabel(portfolio.type)} · {formatEnumLabel(portfolio.category)}
          </div>
          <div className="mt-3 whitespace-pre-wrap text-[13px] leading-[19px] text-white/70">
            {portfolio.description || t.noDescription}
          </div>
        </section>

        {/* Location + Signals */}
        <div className="flex flex-col gap-4 lg:flex-1">
          <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="mb-3 flex items-center gap-2 text-[12px] font-medium text-white/75">
              <MapPin size={13} className="text-white/45" />
              <span>{t.location}</span>
            </div>
            <div className="text-[13px] text-white/80">{locationLabel}</div>

            <div className="mb-2 mt-4 flex items-center gap-2 text-[11px] font-medium text-white/45">
              <Globe size={12} className="text-white/40" />
              <span>{t.websites}</span>
            </div>
            {canEditWebsites ? (
              <>
                <textarea
                  value={foundationDraft.websiteUrlsText}
                  onChange={handleWebsiteUrlsTextChange}
                  rows={4}
                  className="w-full resize-y rounded-md border border-white/10 bg-white/[0.02] p-2 text-[12px] text-white outline-none transition-colors focus:border-white/25"
                  placeholder="https://www.nhood.com&#10;https://www.nhood.fr/actifs-immobiliers/centres-commerciaux"
                />
                <div className="mt-1 text-[10px] text-white/35">{t.websitesHint}</div>
              </>
            ) : (
              <div className="space-y-1">
                {websiteUrls.length > 0 ? (
                  websiteUrls.map((url) => (
                    <div key={url} className="truncate text-[13px] text-white/80">
                      {formatWebsiteLabel(url)}
                    </div>
                  ))
                ) : (
                  <div className="text-[13px] text-white/80">{websiteLabel || t.noWebsite}</div>
                )}
              </div>
            )}
          </section>

          <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="mb-3 flex items-center gap-2 text-[12px] font-medium text-white/75">
              <Target size={13} className="text-white/45" />
              <span>{t.signals}</span>
            </div>
            <div className="flex flex-col gap-3 text-[13px]">
              <div className="flex items-center justify-between gap-2">
                <span className="text-white/50">Intent</span>
                <span className={clsx('rounded-full border px-2 py-0.5 text-[11px] font-semibold', intentBadgeClass(portfolio.intentScore))}>
                  {portfolio.intentScore ?? t.notAvailable}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-white/50">{t.assetsLinked}</span>
                <span className="font-mono text-white/85">{portfolio.realPropertyCount ?? 0}</span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function PortfolioPropertiesTab({ portfolioId }: { portfolioId: string }) {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const portfolio = useKernelStore((state) => state.inventory.Portfolio.registry[portfolioId]);
  const portfolioName = portfolio?.name || '';
  const propertiesViewMode: InventoryVisualMode =
    portfolio?.runtime?.propertiesViewMode === 'table' ? 'table' : 'grid';

  const handleCreateProperty = useCallback(async () => {
    if (!kernel.current) return;
    const name = window.prompt(lang === 'fr' ? 'Nom de la nouvelle propriete :' : 'New property name:');
    if (!name) return;

    try {
      await kernel.current.createEntity('Property', {
        name,
        portfolioId,
      } as Partial<LeasingPropertyDTO>);
    } catch {
      // Error is already handled by toast in useInventory
    }
  }, [kernel, portfolioId, lang]);

  const handlePropertiesViewModeChange = useCallback((mode: InventoryVisualMode) => {
    if (!kernel.current) return;
    kernel.current.patchEntityRuntimeState('Portfolio', portfolioId, {
      propertiesViewMode: mode,
    });
  }, [kernel, portfolioId]);

  const renderHeader = useCallback(
    () => (
      <div className="flex items-center gap-3 text-[12px] text-white/65">
        <ViewModeGraphicToggle
          mode={propertiesViewMode}
          onModeChange={handlePropertiesViewModeChange}
        />
        <span>{portfolioName}</span>
        <span className="text-white/30">-</span>
        <span className="text-white/50">{portfolio?.propertyCount ?? 0} {lang === 'fr' ? 'actifs declares' : 'declared assets'}</span>
      </div>
    ),
    [handlePropertiesViewModeChange, lang, portfolio?.propertyCount, portfolioName, propertiesViewMode]
  );

  const propertiesLockedFilters = useMemo<Partial<LeasingPropertyFilters>>(
    () => ({ owner: portfolioName }),
    [portfolioName]
  );

  if (!portfolioName) {
    return <div className="p-8 text-center text-white/30 animate-pulse">{t.loading}</div>;
  }

  return (
    <div>
      {propertiesViewMode === 'grid' ? (
        <PropertiesGrid
          lockedFilters={propertiesLockedFilters}
          assetLedgerTitle={lang === 'fr' ? 'Actifs du portfolio' : 'Portfolio assets'}
          assetLedgerBreadcrumb={lang === 'fr' ? 'Portfolio / Actifs' : 'Portfolio / Assets'}
          showDefaultToolbarSelectors={false}
          renderCustomToolbarHeader={renderHeader}
          onCreateClick={handleCreateProperty}
          viewModeVisual={propertiesViewMode}
          onViewModeVisualChange={handlePropertiesViewModeChange}
        />
      ) : (
        <PropertiesTable
          lockedFilters={propertiesLockedFilters}
          assetLedgerTitle={lang === 'fr' ? 'Actifs du portfolio' : 'Portfolio assets'}
          assetLedgerBreadcrumb={lang === 'fr' ? 'Portfolio / Actifs' : 'Portfolio / Assets'}
          showDefaultToolbarSelectors={false}
          renderCustomToolbarHeader={renderHeader}
          onCreateClick={handleCreateProperty}
          viewModeVisual={propertiesViewMode}
          onViewModeVisualChange={handlePropertiesViewModeChange}
        />
      )}
    </div>
  );
}

function PortfolioFitCampaignsTab({ portfolioId }: { portfolioId: string }) {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const isFaterOrgMember = useKernelStore((state) => state.userContext.isFaterOrgMember);
  const isOrganisationAdmin = useKernelStore((state) => state.userContext.isOrganisationAdmin);
  const t = translations[lang];
  const portfolio = useKernelStore((state) => state.inventory.Portfolio.registry[portfolioId]);
  const canEditRubric = isFaterOrgMember || isOrganisationAdmin;
  const [isLaunchingCampaign, setIsLaunchingCampaign] = useState(false);
  const [pendingCountryCodes, setPendingCountryCodes] = useState<string[] | null>(null);
  const [isSavingCountryFilters, setIsSavingCountryFilters] = useState(false);
  const [isSavingMinimumNetworkSize, setIsSavingMinimumNetworkSize] = useState(false);

  const rubric = useMemo<ScoringCriterion[]>(
    () => (Array.isArray(portfolio?.scoringRubric) ? portfolio.scoringRubric : []),
    [portfolio?.scoringRubric]
  );
  const portfolioFitCountryCodes = useMemo(
    () => normalizePortfolioFitCountryCodes(portfolio?.portfolioFitCountryCodes),
    [portfolio?.portfolioFitCountryCodes]
  );
  const effectiveCountryCodes = pendingCountryCodes ?? portfolioFitCountryCodes;
  const portfolioFitMinimumNetworkSize = useMemo(() => {
    const raw = portfolio?.portfolioFitMinimumNetworkSize;
    return typeof raw === 'number' && Number.isFinite(raw)
      ? Math.max(1, Math.floor(raw))
      : 1;
  }, [portfolio?.portfolioFitMinimumNetworkSize]);
  const minimumNetworkSizeDraft = useMemo(() => {
    const raw =
      portfolio?.portfolioFitMinimumNetworkSizeDraft ??
      portfolio?.runtime?.portfolioFitMinimumNetworkSizeDraft;
    const fallback = portfolioFitMinimumNetworkSize;
    return typeof raw === 'number' && Number.isFinite(raw)
      ? Math.max(1, Math.floor(raw))
      : fallback;
  }, [
    portfolio?.portfolioFitMinimumNetworkSizeDraft,
    portfolio?.runtime?.portfolioFitMinimumNetworkSizeDraft,
    portfolioFitMinimumNetworkSize,
  ]);

  const budgetCreditsDraft = useMemo(() => {
    const raw =
      portfolio?.portfolioFitBudgetCreditsDraft ??
      portfolio?.runtime?.portfolioFitBudgetCreditsDraft;
    return typeof raw === 'number' && Number.isFinite(raw)
      ? Math.max(0, Math.floor(raw))
      : undefined;
  }, [portfolio?.portfolioFitBudgetCreditsDraft, portfolio?.runtime?.portfolioFitBudgetCreditsDraft]);
  const excludeRecentPortfolioFitsDraft = useMemo(() => {
    const raw =
      portfolio?.portfolioFitExcludeRecentScoresDraft ??
      portfolio?.runtime?.portfolioFitExcludeRecentScoresDraft;
    return typeof raw === 'boolean' ? raw : true;
  }, [portfolio?.portfolioFitExcludeRecentScoresDraft, portfolio?.runtime?.portfolioFitExcludeRecentScoresDraft]);
  const requireKnownWebsiteDraft = useMemo(() => {
    const raw =
      portfolio?.portfolioFitRequireKnownWebsiteDraft ??
      portfolio?.runtime?.portfolioFitRequireKnownWebsiteDraft;
    return typeof raw === 'boolean' ? raw : false;
  }, [portfolio?.portfolioFitRequireKnownWebsiteDraft, portfolio?.runtime?.portfolioFitRequireKnownWebsiteDraft]);

  const portfolioFitStatsScopeKey = useMemo(
    () => buildPortfolioFitStatsScopeKey(
      portfolioId,
      excludeRecentPortfolioFitsDraft,
      requireKnownWebsiteDraft,
      portfolioFitCountryCodes,
      portfolioFitMinimumNetworkSize,
    ),
    [excludeRecentPortfolioFitsDraft, requireKnownWebsiteDraft, portfolioFitCountryCodes, portfolioFitMinimumNetworkSize, portfolioId]
  );

  const portfolioFitStatsEntry = useKernelStore((state) =>
    state.inventoryStats.PortfolioFit[portfolioFitStatsScopeKey] ?? null
  );

  const targetBrandCount = useMemo(
    () => readStatsCardNumber(portfolioFitStatsEntry?.data, 'eligible_brands'),
    [portfolioFitStatsEntry?.data]
  );
  const totalBrandsCount = useMemo(
    () => readStatsCardNumber(portfolioFitStatsEntry?.data, 'total_brands'),
    [portfolioFitStatsEntry?.data]
  );
  const alreadyScoredBrandsCount = useMemo(
    () => readStatsCardNumber(portfolioFitStatsEntry?.data, 'already_scored'),
    [portfolioFitStatsEntry?.data]
  );
  const recentScoredBrandsCount = useMemo(
    () => readStatsCardNumber(portfolioFitStatsEntry?.data, 'recent_scored'),
    [portfolioFitStatsEntry?.data]
  );
  const isBrandCountLoading = Boolean(
    portfolioFitStatsEntry?.isLoading || (!portfolioFitStatsEntry?.data && portfolio?.id)
  );

  const maxTargetableBrands = useMemo(
    () => Math.max(0, targetBrandCount ?? 0),
    [targetBrandCount]
  );

  const allocatedBudgetCredits = maxTargetableBrands <= 0
    ? 0
    : Math.max(1, budgetCreditsDraft ?? maxTargetableBrands);

  const targetedBrandsCount = Math.min(maxTargetableBrands, allocatedBudgetCredits);

  const etaSeconds = useMemo(() => {
    if (targetedBrandsCount === 0) return 0;
    const brandsPerSecond = 20;
    return Math.ceil(targetedBrandsCount / brandsPerSecond);
  }, [targetedBrandsCount]);

  const projectedEndTime = useMemo(() => {
    if (etaSeconds <= 0) return null;
    // Wall clock on purpose: this is a countdown's end time, which must be computed from now.
    // eslint-disable-next-line react-hooks/purity
    const end = new Date(Date.now() + etaSeconds * 1000);
    return end.toLocaleTimeString(lang === 'fr' ? 'fr-FR' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }, [etaSeconds, lang]);

  const patchPortfolioRuntime = useCallback(
    (patch: Partial<Pick<PortfolioRuntimeState, 'portfolioFitBudgetCreditsDraft' | 'portfolioFitExcludeRecentScoresDraft' | 'portfolioFitRequireKnownWebsiteDraft' | 'portfolioFitMinimumNetworkSizeDraft'>>) => {
      if (!kernel.current) return;
      kernel.current.patchEntityRuntimeState('Portfolio', portfolioId, patch);
    },
    [kernel, portfolioId]
  );

  const refreshTotalBrandCount = useCallback(async (options?: { excludeRecentPortfolioFits?: boolean; requireKnownWebsite?: boolean; force?: boolean }) => {
    if (!kernel.current) return;
    const effectiveExcludeRecent = options?.excludeRecentPortfolioFits ?? excludeRecentPortfolioFitsDraft;
    const effectiveRequireKnownWebsite = options?.requireKnownWebsite ?? requireKnownWebsiteDraft;
    const scopeKey = buildPortfolioFitStatsScopeKey(
      portfolioId,
      effectiveExcludeRecent,
      effectiveRequireKnownWebsite,
      portfolioFitCountryCodes,
      portfolioFitMinimumNetworkSize,
    );

    try {
      await kernel.current.fetchInventoryStats('PortfolioFit', {
        force: options?.force === true,
        scopeKey,
        includeOrganisationIdParam: false,
        queryParams: {
          portfolioId,
          excludeRecentPortfolioFits: effectiveExcludeRecent ? 'true' : 'false',
          requireKnownWebsite: effectiveRequireKnownWebsite ? 'true' : 'false',
        },
      });
    } catch (error) {
      console.error('[PortfolioFitCampaignsTab] Failed to load brand count', error);
    }
  }, [excludeRecentPortfolioFitsDraft, requireKnownWebsiteDraft, kernel, portfolioFitCountryCodes, portfolioFitMinimumNetworkSize, portfolioId]);

  useEffect(() => {
    if (!portfolio?.id) return;
    if (portfolioFitStatsEntry?.data || portfolioFitStatsEntry?.isLoading) {
      if (!portfolioFitStatsEntry?.stale) return;
    }
    void refreshTotalBrandCount({
      excludeRecentPortfolioFits: excludeRecentPortfolioFitsDraft,
      requireKnownWebsite: requireKnownWebsiteDraft,
      force: Boolean(portfolioFitStatsEntry?.stale),
    });
  }, [
    excludeRecentPortfolioFitsDraft,
    requireKnownWebsiteDraft,
    portfolio?.id,
    portfolioFitStatsEntry?.data,
    portfolioFitStatsEntry?.isLoading,
    portfolioFitStatsEntry?.stale,
    refreshTotalBrandCount,
  ]);

  useEffect(() => {
    if (maxTargetableBrands <= 0) {
      if (budgetCreditsDraft === undefined || budgetCreditsDraft === 0) return;
      patchPortfolioRuntime({ portfolioFitBudgetCreditsDraft: 0 });
      return;
    }

    const nextBudget = budgetCreditsDraft === undefined || budgetCreditsDraft <= 0
      ? maxTargetableBrands
      : Math.max(1, budgetCreditsDraft);
    if (nextBudget === budgetCreditsDraft) return;
    patchPortfolioRuntime({ portfolioFitBudgetCreditsDraft: nextBudget });
  }, [budgetCreditsDraft, maxTargetableBrands, patchPortfolioRuntime]);

  const handleRubricChange = useCallback(
    async (nextRubric: ScoringCriterion[]) => {
      if (!canEditRubric) return;
      if (!kernel.current) return;
      await kernel.current.mutateInventory(
        [
          {
            action: 'UPDATE',
            entity: 'Portfolio',
            id: portfolioId,
            payload: { scoringRubric: nextRubric } as Partial<PortfolioInventoryEntityState>,
          },
        ],
        'Update portfolio scoring rubric'
      );
    },
    [canEditRubric, kernel, portfolioId]
  );

  const handleBudgetCreditsChange = useCallback(
    (value: number) => {
      const normalized = maxTargetableBrands <= 0
        ? 0
        : Math.max(1, Math.floor(value));
      patchPortfolioRuntime({ portfolioFitBudgetCreditsDraft: normalized });
    },
    [maxTargetableBrands, patchPortfolioRuntime]
  );
  const handleExcludeRecentPortfolioFitsChange = useCallback(
    (checked: boolean) => {
      patchPortfolioRuntime({ portfolioFitExcludeRecentScoresDraft: checked });
    },
    [patchPortfolioRuntime]
  );
  const handleRequireKnownWebsiteChange = useCallback(
    (checked: boolean) => {
      patchPortfolioRuntime({ portfolioFitRequireKnownWebsiteDraft: checked });
    },
    [patchPortfolioRuntime]
  );

  const handleMinimumNetworkSizeDraftChange = useCallback(
    (value: string) => {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) {
        patchPortfolioRuntime({ portfolioFitMinimumNetworkSizeDraft: 1 });
        return;
      }
      patchPortfolioRuntime({
        portfolioFitMinimumNetworkSizeDraft: Math.max(1, Math.floor(parsed)),
      });
    },
    [patchPortfolioRuntime]
  );

  const persistMinimumNetworkSize = useCallback(async () => {
    if (!canEditRubric || !kernel.current) return;
    const persisted = portfolioFitMinimumNetworkSize;
    const next = Math.max(1, Math.floor(minimumNetworkSizeDraft));
    patchPortfolioRuntime({ portfolioFitMinimumNetworkSizeDraft: next });
    if (next === persisted) return;

    setIsSavingMinimumNetworkSize(true);
    try {
      // The write, its normalization and its error toast live in the kernel, so the same value can
      // be set from chat or a shortcut; the draft rollback is the only part that is UI state.
      const saved = await kernel.current.savePortfolioFitMinimumNetworkSize(portfolioId, next);
      if (!saved) {
        patchPortfolioRuntime({ portfolioFitMinimumNetworkSizeDraft: persisted });
        return;
      }
      await refreshTotalBrandCount({
        excludeRecentPortfolioFits: excludeRecentPortfolioFitsDraft,
        requireKnownWebsite: requireKnownWebsiteDraft,
        force: true,
      });
    } finally {
      setIsSavingMinimumNetworkSize(false);
    }
  }, [
    canEditRubric,
    excludeRecentPortfolioFitsDraft,
    requireKnownWebsiteDraft,
    kernel,
    minimumNetworkSizeDraft,
    patchPortfolioRuntime,
    portfolioFitMinimumNetworkSize,
    portfolioId,
    refreshTotalBrandCount,
  ]);

  const persistCountryFilters = useCallback(async (nextCodes: string[]) => {
    if (!canEditRubric || !kernel.current) return;
    const normalizedCodes = normalizePortfolioFitCountryCodes(nextCodes);
    const currentCodes = normalizePortfolioFitCountryCodes(portfolioFitCountryCodes);
    if (
      normalizedCodes.length === currentCodes.length &&
      normalizedCodes.every((code, index) => code === currentCodes[index])
    ) {
      setPendingCountryCodes(null);
      return;
    }

    setPendingCountryCodes(normalizedCodes);
    setIsSavingCountryFilters(true);
    try {
      // Same split as the minimum network size above: the kernel writes and reports, the component
      // only owns the pending flags it painted optimistically.
      const saved = await kernel.current.savePortfolioFitCountryCodes(portfolioId, normalizedCodes);
      if (!saved) {
        setPendingCountryCodes(null);
        return;
      }
      await refreshTotalBrandCount({
        excludeRecentPortfolioFits: excludeRecentPortfolioFitsDraft,
        requireKnownWebsite: requireKnownWebsiteDraft,
        force: true,
      });
    } finally {
      setIsSavingCountryFilters(false);
    }
  }, [
    canEditRubric,
    excludeRecentPortfolioFitsDraft,
    requireKnownWebsiteDraft,
    kernel,
    portfolioFitCountryCodes,
    portfolioId,
    refreshTotalBrandCount,
  ]);

  useEffect(() => {
    if (!pendingCountryCodes) return;
    const currentCodes = normalizePortfolioFitCountryCodes(portfolioFitCountryCodes);
    if (
      currentCodes.length === pendingCountryCodes.length &&
      currentCodes.every((code, index) => code === pendingCountryCodes[index])
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing when this state settles is a behavioural change, not merge work
      setPendingCountryCodes(null);
    }
  }, [pendingCountryCodes, portfolioFitCountryCodes]);


  const handleToggleCountryFilter = useCallback((countryCode: string) => {
    if (!canEditRubric || isSavingCountryFilters) return;
    const normalizedCode = countryCode.trim().toUpperCase();
    if (!normalizedCode) return;

    const source = normalizePortfolioFitCountryCodes(effectiveCountryCodes);
    const nextSet = new Set(source);
    if (nextSet.has(normalizedCode)) {
      nextSet.delete(normalizedCode);
    } else {
      nextSet.add(normalizedCode);
    }
    void persistCountryFilters(Array.from(nextSet));
  }, [canEditRubric, isSavingCountryFilters, effectiveCountryCodes, persistCountryFilters]);

  const handleMinimumNetworkSizeInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleMinimumNetworkSizeDraftChange(event.target.value);
    },
    [handleMinimumNetworkSizeDraftChange]
  );

  const handleMinimumNetworkSizeBlur = useCallback(() => {
    void persistMinimumNetworkSize();
  }, [persistMinimumNetworkSize]);

  const handleMinimumNetworkSizeKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.currentTarget.blur();
  }, []);

  const eligibilityStats = useMemo(
    () => [
      { label: t.totalBrands, value: isBrandCountLoading ? t.loadingCount : (totalBrandsCount ?? '-') },
      { label: t.alreadyScored, value: isBrandCountLoading ? t.loadingCount : (alreadyScoredBrandsCount ?? '-') },
      { label: t.recentScores, value: isBrandCountLoading ? t.loadingCount : (recentScoredBrandsCount ?? '-') },
      { label: t.eligibleBrands, value: isBrandCountLoading ? t.loadingCount : maxTargetableBrands },
    ],
    [
      alreadyScoredBrandsCount,
      isBrandCountLoading,
      maxTargetableBrands,
      recentScoredBrandsCount,
      t.alreadyScored,
      t.eligibleBrands,
      t.loadingCount,
      t.recentScores,
      t.totalBrands,
      totalBrandsCount,
    ]
  );

  const excludeRecentPortfolioFitsLabel = useMemo(
    () => <span className="text-[12px] text-white/70">{t.excludeRecentPortfolioFits}</span>,
    [t.excludeRecentPortfolioFits]
  );
  const requireKnownWebsiteLabel = useMemo(
    () => <span className="text-[12px] text-white/70">{t.requireKnownWebsite}</span>,
    [t.requireKnownWebsite]
  );

  const campaignsLockedFilters = useMemo<Partial<CampaignFilters>>(
    () => ({ portfolioId, targetKind: 'PORTFOLIO' }),
    [portfolioId]
  );

  const renderCampaignToolbarHeader = useCallback(
    () => (
      <div className="min-w-0 text-right">
        <div className="text-[12px] font-medium text-white/90">{t.qualification}</div>
        <div className="text-[11px] text-white/45">{t.campaignsSubtitle}</div>
      </div>
    ),
    [t.campaignsSubtitle, t.qualification]
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-[#2A2A2A] px-4 py-4">
        <div className="flex flex-col gap-3">
          {/* Scoring strategy */}
          <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-[12px] font-medium text-white/75">{t.scoringRubric}</div>
            {!canEditRubric && <div className="mt-1 text-[11px] text-amber-300/80">{t.rubricReadOnly}</div>}
            <div className="mt-3">
              <ScoringRubricEditor
                value={rubric}
                onChange={handleRubricChange}
                defaultRubric={DEFAULT_PORTFOLIO_SCORING_RUBRIC}
              />
            </div>
          </section>

          {/* Country filters */}
          <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="mb-3 text-[12px] font-medium text-white/75">{t.countryFiltersLabel}</div>
            <div className="flex flex-col gap-4 xl:flex-row xl:items-stretch">
              <div className="flex flex-col gap-3 xl:w-[300px] xl:min-w-[300px]">
                <div className="flex flex-wrap items-center gap-2">
                  {eligibilityStats.map((stat) => (
                    <span
                      key={stat.label}
                      className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.03] px-2.5 py-1"
                    >
                      <span className="text-[11px] text-white/45">{stat.label}</span>
                      <span className="text-[12px] font-mono text-white/85">{stat.value}</span>
                    </span>
                  ))}
                </div>
                <div className="text-[10px] text-white/45">
                  {isSavingCountryFilters ? t.countryFiltersSaving : t.countryFiltersHint}
                </div>
                <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-white/55">
                  <span>{t.selectedCountriesLabel}:</span>
                  {effectiveCountryCodes.length === 0 ? (
                    <span className="text-white/40">{t.countryFiltersAllCountries}</span>
                  ) : (
                    effectiveCountryCodes.map((code) => (
                      <span key={code} className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-300">
                        {code}
                      </span>
                    ))
                  )}
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <PortfolioAssetsCountryMap
                  propertyIds={portfolio?.propertyIds ?? EMPTY_PROPERTY_IDS}
                  selectedCountryCodes={effectiveCountryCodes}
                  onToggleCountry={handleToggleCountryFilter}
                  disabled={!canEditRubric || isSavingCountryFilters}
                  heightClassName="h-[560px] min-h-[560px]"
                />
              </div>
            </div>
          </section>

          {/* Eligibility & launch */}
          <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
              <div>
                <div className="mb-1.5 flex items-center justify-between gap-3">
                  <div className="text-[12px] text-white/70">{t.minimumNetworkSizeLabel}</div>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={minimumNetworkSizeDraft}
                    onChange={handleMinimumNetworkSizeInputChange}
                    onBlur={handleMinimumNetworkSizeBlur}
                    onKeyDown={handleMinimumNetworkSizeKeyDown}
                    disabled={!canEditRubric || isSavingMinimumNetworkSize}
                    className="h-8 w-[110px] rounded-md border border-white/10 bg-white/[0.03] px-2 text-[12px] text-white/90 outline-none focus:border-white/30 disabled:cursor-not-allowed disabled:text-white/40"
                  />
                </div>
                <div className="text-[10px] text-white/45">
                  {isSavingMinimumNetworkSize ? t.minimumNetworkSizeSaving : t.minimumNetworkSizeHint}
                </div>
              </div>

              <div className="flex flex-col gap-2.5">
                <Switch
                  checked={excludeRecentPortfolioFitsDraft}
                  onCheckedChange={handleExcludeRecentPortfolioFitsChange}
                  labelRight={excludeRecentPortfolioFitsLabel}
                />
                <Switch
                  checked={requireKnownWebsiteDraft}
                  onCheckedChange={handleRequireKnownWebsiteChange}
                  labelRight={requireKnownWebsiteLabel}
                />
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-4 border-t border-white/10 pt-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0 w-full sm:max-w-[340px]">
                <div className="mb-1 text-[10px] text-white/40">{t.budgetAllocationLabel}</div>
                <RangeControl
                  hideLabel
                  showValueBox
                  min={maxTargetableBrands > 0 ? 1 : 0}
                  max={SOURCING_BUDGET_MAX}
                  step={1}
                  value={allocatedBudgetCredits}
                  onValueChange={handleBudgetCreditsChange}
                />
                <div className="mt-1 text-[10px] text-white/35">
                  {describeSourceBudget(lang, allocatedBudgetCredits, 1)} ({allocatedBudgetCredits} / {maxTargetableBrands} {t.creditsLabel})
                </div>
              </div>

              <div className="flex items-end gap-4">
                {targetedBrandsCount > 0 && (
                  <>
                    <div className="flex flex-col">
                      <span className="text-[10px] text-white/40">{t.estimatedDurationLabel}</span>
                      <span className="text-[12px] font-mono text-white">
                        {etaSeconds < 60 ? '< 1 min' : `~${Math.ceil(etaSeconds / 60)} min`}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] text-white/40">{t.estimatedEndTimeLabel}</span>
                      <span className="text-[12px] font-mono text-white">{projectedEndTime ?? '--:--'}</span>
                    </div>
                  </>
                )}
                {/* The portfolio-wide launch button was removed on 2026-08-18 with the engine behind
                    it: portfolio sourcing was retired from the product on 2026-08-16 and its route
                    is gone, so this control could only fail. A sourcing is launched from an asset. */}
              </div>
            </div>
          </section>
        </div>
      </div>
      <div className="min-h-0 flex-1 p-4">
        <CampaignsTable
          lockedFilters={campaignsLockedFilters}
          assetLedgerTitle={lang === 'fr' ? 'Campagnes portfolio fit' : 'Portfolio fit campaigns'}
          assetLedgerBreadcrumb={lang === 'fr' ? 'Portfolio / Qualification' : 'Portfolio / Qualification'}
          renderCustomToolbarHeader={renderCampaignToolbarHeader}
        />
      </div>
    </div>
  );
}

function PortfolioFitsTab({ portfolioId }: { portfolioId: string }) {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const portfolio = useKernelStore((state) => state.inventory.Portfolio.registry[portfolioId]);
  const portfolioFitsViewMode: InventoryVisualMode =
    portfolio?.runtime?.portfolioFitsViewMode === 'table' ? 'table' : 'grid';

  const handlePortfolioFitsViewModeChange = useCallback((mode: InventoryVisualMode) => {
    if (!kernel.current) return;
    kernel.current.patchEntityRuntimeState('Portfolio', portfolioId, {
      portfolioFitsViewMode: mode,
    });
  }, [kernel, portfolioId]);

  const portfolioFitsLockedFilters = useMemo<Partial<PortfolioFitFilters>>(
    () => ({ portfolioId }),
    [portfolioId]
  );

  const renderFitsToolbarHeader = useCallback(
    () => (
      <div className="min-w-0 text-right">
        <div className="text-[12px] font-medium text-white/90">{t.fits}</div>
        <div className="text-[11px] text-white/45">{t.fitsSubtitle}</div>
      </div>
    ),
    [t.fits, t.fitsSubtitle]
  );

  return (
    <div className="h-full">
      {portfolioFitsViewMode === 'grid' ? (
        <PortfolioFitsGrid
          initialFilters={PORTFOLIO_FITS_INITIAL_FILTERS}
          lockedFilters={portfolioFitsLockedFilters}
          assetLedgerTitle={lang === 'fr' ? 'Portfolio fits' : 'Portfolio fits'}
          assetLedgerBreadcrumb={lang === 'fr' ? 'Portfolio / Fits' : 'Portfolio / Fits'}
          renderCustomToolbarHeader={renderFitsToolbarHeader}
          viewModeVisual={portfolioFitsViewMode}
          onViewModeVisualChange={handlePortfolioFitsViewModeChange}
        />
      ) : (
        <PortfolioFitsTable
          initialFilters={PORTFOLIO_FITS_INITIAL_FILTERS}
          lockedFilters={portfolioFitsLockedFilters}
          assetLedgerTitle={lang === 'fr' ? 'Portfolio fits' : 'Portfolio fits'}
          assetLedgerBreadcrumb={lang === 'fr' ? 'Portfolio / Fits' : 'Portfolio / Fits'}
          renderCustomToolbarHeader={renderFitsToolbarHeader}
          viewModeVisual={portfolioFitsViewMode}
          onViewModeVisualChange={handlePortfolioFitsViewModeChange}
        />
      )}
    </div>
  );
}

export const PortfolioPage = ({ portfolioId, className, hasEntityRowAbove = false }: PortfolioPageProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const portfolio = useKernelStore(
    (state) => (state.inventory.Portfolio.registry[portfolioId] as PortfolioInventoryEntityState | undefined) ?? null
  );
  // About tab removed from the portfolio view.
  const canSeeAboutTab = false;
  const defaultTab: PortfolioWorkspaceTab = 'PORTFOLIO_FIT_CAMPAIGNS';
  const activeTabCandidate = portfolio?.activeTab ?? portfolio?.runtime?.activeTab;
  const normalizedActiveTab = normalizePortfolioWorkspaceTab(activeTabCandidate);
  const isActiveTabAccessible =
    normalizedActiveTab !== undefined &&
    (canSeeAboutTab || normalizedActiveTab !== 'FOUNDATION');
  const activeTab = isActiveTabAccessible ? normalizedActiveTab : defaultTab;

  const patchKernelState = useCallback(
    (patch: Partial<Pick<PortfolioRuntimeState, 'activeTab'>>) => {
      if (!kernel.current) return;
      kernel.current.patchEntityRuntimeState('Portfolio', portfolioId, patch);
    },
    [kernel, portfolioId]
  );

  useEffect(() => {
    if (!kernel.current) return;
    const normalized = normalizePortfolioWorkspaceTab(activeTabCandidate);
    const isAccessible = normalized !== undefined && (canSeeAboutTab || normalized !== 'FOUNDATION');
    if (isAccessible) return;
    kernel.current.patchEntityRuntimeState('Portfolio', portfolioId, {
      activeTab: defaultTab,
    });
  }, [kernel, portfolioId, activeTabCandidate, canSeeAboutTab, defaultTab]);

  useEffect(() => {
    if (!kernel.current || !portfolioId) return;
    if (!portfolio || portfolio.propertyCount === undefined || portfolio.scoringRubric === undefined) {
      void kernel.current.syncEntity('Portfolio', portfolioId);
    }
  }, [kernel, portfolioId, portfolio]);

  const tabs = useMemo(() => {
    const orderedTabs: Array<{ id: PortfolioWorkspaceTab; label: string }> = [
      { id: 'PORTFOLIO_FIT_CAMPAIGNS', label: t.qualification },
      { id: 'PROPERTIES', label: t.properties },
      { id: 'PORTFOLIO_FITS', label: t.fits },
    ];
    if (canSeeAboutTab) {
      orderedTabs.push({ id: 'FOUNDATION', label: t.about });
    }
    return orderedTabs;
  }, [canSeeAboutTab, t.about, t.fits, t.properties, t.qualification]);
  const handleTabChange = useCallback(
    (nextTab: PortfolioWorkspaceTab) => {
      patchKernelState({ activeTab: nextTab });
    },
    [patchKernelState]
  );

  return (
    <div className={clsx('flex min-h-[620px] flex-col bg-[#141414] text-white', className)}>
      {!hasEntityRowAbove && <PortfolioOverviewHeader portfolioId={portfolioId} />}
      <EntityPageTabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} sticky />

      <div className="min-h-0 flex-1 overflow-y-auto">
        {canSeeAboutTab && activeTab === 'FOUNDATION' && <PortfolioFoundationTab portfolioId={portfolioId} />}
        {activeTab === 'PROPERTIES' && <PortfolioPropertiesTab portfolioId={portfolioId} />}
        {activeTab === 'PORTFOLIO_FIT_CAMPAIGNS' && <PortfolioFitCampaignsTab portfolioId={portfolioId} />}
        {activeTab === 'PORTFOLIO_FITS' && <PortfolioFitsTab portfolioId={portfolioId} />}
      </div>
    </div>
  );
};
